// ... existing code ...

import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Faltan las variables de entorno de Supabase');
}

// Cliente único con persistencia de sesión y auto-refresh
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storageKey: 'sb-auth-token',
  },
});

// Variable para evitar múltiples redirecciones
let isHandlingAuthError = false;

// Función para limpiar sesión inválida
const handleInvalidSession = async () => {
  if (isHandlingAuthError) return;
  isHandlingAuthError = true;

  console.warn('⚠️ Sesión inválida detectada, limpiando...');

  try {
    await supabase.auth.signOut({ scope: 'local' });
  } catch (error) {
    // Ignorar errores al cerrar sesión
  }

  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.includes('supabase') || key.includes('sb-')) {
        localStorage.removeItem(key);
      }
    });
    const sessionKeys = Object.keys(sessionStorage);
    sessionKeys.forEach(key => {
      if (key.includes('supabase') || key.includes('sb-')) {
        sessionStorage.removeItem(key);
      }
    });
  } catch (e) {
    // Ignorar errores de storage
  }

  const isAdminRoute = window.location.pathname.startsWith('/admin') && !window.location.pathname.includes('/login');
  const isClientPortal = window.location.pathname.startsWith('/client');

  if (isAdminRoute) {
    setTimeout(() => {
      window.location.href = '/admin/login';
    }, 100);
  } else if (isClientPortal) {
    setTimeout(() => {
      window.location.href = '/client/portal';
    }, 100);
  }

  setTimeout(() => {
    isHandlingAuthError = false;
  }, 5000);
};

// Listener para manejar cambios de autenticación
supabase.auth.onAuthStateChange(async (event, session) => {
  if (event === 'TOKEN_REFRESHED') {
    console.log('✅ Token refrescado correctamente');
  }
  if (event === 'SIGNED_OUT') {
    console.log('🚪 Sesión cerrada');
  }
  if (event === 'USER_UPDATED' && !session) {
    await handleInvalidSession();
  }
});

// Helper: verifica si un error/string contiene mensaje de refresh token inválido
const isRefreshTokenError = (text: string): boolean => {
  if (!text) return false;
  const msg = text.toLowerCase();
  return (
    msg.includes('refresh token') ||
    msg.includes('invalid refresh') ||
    msg.includes('refresh_token_not_found') ||
    msg.includes('token not found') ||
    msg.includes('invalid token') ||
    msg.includes('session not found')
  );
};

// Interceptor global para capturar errores de autenticación en fetch
const originalFetch = window.fetch;
window.fetch = async (...args) => {
  try {
    const response = await originalFetch(...args);

    if (args[0]?.toString().includes(supabaseUrl) && (response.status === 400 || response.status === 401 || response.status === 403)) {
      const clonedResponse = response.clone();
      try {
        const data = await clonedResponse.json();
        const errorText = JSON.stringify(data);
        if (isRefreshTokenError(errorText)) {
          console.warn('🔒 Token inválido detectado en fetch');
          await handleInvalidSession();
        }
      } catch (e) {
        // Ignorar errores al parsear JSON
      }
    }

    return response;
  } catch (error: any) {
    if (isRefreshTokenError(error?.message || '')) {
      console.warn('🔒 Error de autenticación en fetch');
      await handleInvalidSession();
    }
    throw error;
  }
};

// Interceptor para errores no capturados
window.addEventListener('unhandledrejection', async (event) => {
  const error = event.reason;
  const errorMsg = error?.message || '';
  const errorText = JSON.stringify(error);

  if (isRefreshTokenError(errorMsg) || isRefreshTokenError(errorText)) {
    console.warn('🔒 Error de autenticación no capturado');
    event.preventDefault();
    await handleInvalidSession();
  }
});

// Limpieza proactiva al cargar la página
// Si detectamos una sesión corrupta al inicio, la limpiamos sin crash
(async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    if (error) {
      const errMsg = error.message || '';
      if (isRefreshTokenError(errMsg)) {
        console.warn('🔒 Sesión corrupta detectada al inicio, limpiando...');
        await handleInvalidSession();
      }
    }
  } catch (e: any) {
    if (isRefreshTokenError(e?.message || '')) {
      await handleInvalidSession();
    }
  }
})();

// ... existing code ...